
import { useState, useEffect } from 'react';
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";
import errorMonitoring from "@/utils/errorMonitoring";

// Email validation schema
export const inviteFormSchema = z.object({
  email: z.string().email({ message: "Please enter a valid email address" }),
  makeAdmin: z.boolean().default(false)
});

export type InviteFormValues = z.infer<typeof inviteFormSchema>;

export interface Invite {
  code: string;
  email: string;
  status: string;
  created_at: string;
  expires_at: string;
  accepted_at?: string;
  make_admin: boolean;
}

export const useInvites = () => {
  const [invites, setInvites] = useState<Invite[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  
  // Fetch invites when component mounts
  useEffect(() => {
    fetchInvites();
  }, []);
  
  // Fetch invites from Supabase
  const fetchInvites = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('invites')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        throw error;
      }
      
      setInvites(data || []);
    } catch (error: any) {
      console.error('Error fetching invites:', error);
      errorMonitoring.captureError(error);
      setError(`Failed to load invites: ${error.message || 'Unknown error'}`);
      toast.error('Failed to load invites');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Create a new invite
  const handleCreateInvite = async (values: InviteFormValues) => {
    try {
      setIsLoading(true);
      setError(null);
      
      // Call the create_invite function in Supabase with matching type definition parameters
      const { data, error } = await supabase.rpc('create_invite', {
        email: values.email,
        expires_in_days: 7,
        make_admin: values.makeAdmin
      });
      
      if (error) {
        throw error;
      }
      
      toast.success(`Invite sent to ${values.email}`);
      setShowInviteDialog(false);
      fetchInvites(); // Refresh the invites list
    } catch (error: any) {
      console.error('Error creating invite:', error);
      errorMonitoring.captureError(error);
      setError(`Failed to create invite: ${error.message || 'Unknown error'}`);
      toast.error('Failed to create invite');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Revoke an invite
  const handleRevokeInvite = async (code: string) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const { error } = await supabase.rpc('revoke_invite', {
        code: code
      });
      
      if (error) {
        throw error;
      }
      
      toast.success('Invite revoked successfully');
      fetchInvites(); // Refresh the invites list
    } catch (error: any) {
      console.error('Error revoking invite:', error);
      errorMonitoring.captureError(error);
      setError(`Failed to revoke invite: ${error.message || 'Unknown error'}`);
      toast.error('Failed to revoke invite');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    invites,
    isLoading,
    error,
    showInviteDialog,
    setShowInviteDialog,
    handleCreateInvite,
    handleRevokeInvite,
    fetchInvites
  };
};
